import { useState } from "react";
import { submitEnquiry } from "../../api/apiServices";
import { toast } from "react-toastify";

const CATEGORIES = [
  { id: "Account Issue", icon: "👤", desc: "Login, profile, or account access" },
  { id: "Payment Problem", icon: "💳", desc: "Charges, billing, or transactions" },
  { id: "Technical Support", icon: "⚙️", desc: "Bugs, errors, or app issues" },
  { id: "Product Inquiry", icon: "📦", desc: "Questions about our products" },
  { id: "Refund Request", icon: "↩️", desc: "Request a refund or reversal" },
  { id: "Order Issue", icon: "🛒", desc: "Missing, wrong, or delayed order" },
  { id: "Other", icon: "💬", desc: "Something else entirely" },
];

function SupportEnquiryForm({ user, onEnquirySubmitted }) {
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = pick category, 2 = describe

  const handleSubmit = async () => {
    if (!category || !description.trim()) return toast.warn("Please fill all fields");
    setLoading(true);
    try {
      const res = await submitEnquiry(user.userId, category, description.trim());
      toast.success("Enquiry submitted!");
      onEnquirySubmitted(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit enquiry.");
    } finally {
      setLoading(false);
    }
  };

  const selected = CATEGORIES.find(c => c.id === category);

  return (
    <div className="flex-1 flex flex-col overflow-y-auto bg-slate-50">
      {/* Top hero strip */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 px-6 pt-8 pb-12 text-white text-center">
        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-3">
          🎧
        </div>
        <h2 className="text-2xl font-bold mb-1">How can we help?</h2>
        <p className="text-emerald-100 text-sm">We typically respond within a few minutes</p>
      </div>

      {/* Card */}
      <div className="flex-1 -mt-6 mx-4 bg-white rounded-3xl shadow-lg overflow-hidden mb-6">

        {/* Step indicator */}
        <div className="flex items-center gap-0 border-b border-slate-100">
          {["Choose topic", "Describe issue"].map((label, i) => {
            const stepNum = i + 1;
            const isActive = step === stepNum;
            const isDone = step > stepNum;
            return (
              <button
                key={label}
                onClick={() => isDone && setStep(stepNum)}
                className={`flex-1 py-4 text-xs font-semibold flex items-center justify-center gap-2 transition-colors ${
                  isActive ? "text-emerald-600 border-b-2 border-emerald-600" :
                  isDone ? "text-slate-400 cursor-pointer hover:text-emerald-500" :
                  "text-slate-300 cursor-default"
                }`}
              >
                <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold ${
                  isActive ? "bg-emerald-600 text-white" :
                  isDone ? "bg-emerald-100 text-emerald-600" :
                  "bg-slate-100 text-slate-400"
                }`}>
                  {isDone ? "✓" : stepNum}
                </span>
                {label}
              </button>
            );
          })}
        </div>

        {/* Step 1 — Category grid */}
        {step === 1 && (
          <div className="p-5">
            <p className="text-slate-500 text-sm mb-4">Select the topic that best matches your issue</p>
            <div className="grid grid-cols-1 gap-2.5">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => { setCategory(cat.id); setStep(2); }}
                  className={`flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${
                    category === cat.id
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-100 bg-slate-50 hover:border-emerald-200 hover:bg-emerald-50/50"
                  }`}
                >
                  <span className="text-2xl w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                    {cat.icon}
                  </span>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{cat.id}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{cat.desc}</p>
                  </div>
                  <svg className="w-4 h-4 text-slate-300 ml-auto flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — Describe */}
        {step === 2 && (
          <div className="p-5 flex flex-col gap-5">
            {/* Selected category pill */}
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3">
              <span className="text-xl">{selected?.icon}</span>
              <div className="flex-1">
                <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Selected Topic</p>
                <p className="text-slate-800 font-medium text-sm">{selected?.id}</p>
              </div>
              <button
                onClick={() => setStep(1)}
                className="text-xs text-slate-400 hover:text-emerald-600 underline transition-colors"
              >
                Change
              </button>
            </div>

            {/* Textarea */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Tell us what's happening
              </label>
              <div className={`relative rounded-2xl border-2 transition-colors ${
                description.trim().length > 0 ? "border-emerald-400" : "border-slate-200"
              }`}>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  className="w-full p-4 bg-transparent outline-none resize-none text-slate-800 text-sm placeholder-slate-400 rounded-2xl"
                  placeholder="Please describe your issue in as much detail as possible. The more context you give, the faster we can help…"
                />
                <div className="absolute bottom-3 right-4 text-xs text-slate-300">
                  {description.length} chars
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
              <p className="text-xs font-semibold text-blue-600 mb-2">💡 Tips for faster help</p>
              <ul className="text-xs text-blue-500 space-y-1 list-disc list-inside">
                <li>Include any error messages you saw</li>
                <li>Mention when the issue started</li>
                <li>Add your transaction ID if relevant</li>
              </ul>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={loading || !description.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white py-4 rounded-2xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Submit & Start Chat
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SupportEnquiryForm;